import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * ---- PATCH HELPERS ----
   */

  /**
   * Remove blur/loading/transition states that can get stuck during variant swap.
   * @param {HTMLElement} root
   */
  static #clearLoadingState(root) {
    if (!root) return;

    // common loading/transition classes (adjust if your theme uses different ones)
    const classesToRemove = [
      'is-loading',
      'loading',
      'media-gallery--loading',
      'is-transitioning',
      'transitioning',
      'is-animating',
    ];

    classesToRemove.forEach((c) => root.classList.remove(c));
    root.removeAttribute('data-loading');

    // Clear inline blur/opacity on wrappers and images
    root
      .querySelectorAll(
        'img.product-media__image, img, .product-media, slideshow-container, slideshow-slides, slideshow-slide, slideshow-component'
      )
      .forEach((el) => {
        if (!el || !el.style) return;
        if (el.style.filter) el.style.filter = '';
        if (el.style.opacity) el.style.opacity = '';
        // Don't aggressively remove transform (slider uses transforms)
      });
  }

  /**
   * Preload and upgrade images using data_max_resolution (1920) so they become sharp quickly.
   * By default upgrades ALL product images in the gallery (safe and simple).
   * @param {HTMLElement} root
   */
  static #upgradeImagesToMax(root) {
    if (!root) return;

    const imgs = Array.from(root.querySelectorAll('img.product-media__image'));
    if (!imgs.length) return;

    imgs.forEach((img) => {
      const maxSrc = img.getAttribute('data_max_resolution') || img.getAttribute('data-max-resolution');
      if (!maxSrc) return;

      // If it's already max, skip
      const cur = img.currentSrc || img.src || '';
      if (cur.includes('width=1920')) return;

      // Preload to avoid flicker
      const pre = new Image();
      pre.onload = () => {
        // Only swap if still not max (avoid race)
        const now = img.currentSrc || img.src || '';
        if (!now.includes('width=1920')) {
          img.src = maxSrc;

          // Optional: if your theme keeps picking small candidates from srcset,
          // uncomment next line to force the src you just set:
          // img.removeAttribute('srcset');
        }
      };
      pre.src = maxSrc;
    });
  }

  /**
   * Wait for images to finish loading (or error) then clear loading state again.
   * Helps if theme adds blur until load events fire.
   * @param {HTMLElement} root
   */
  static #waitImagesThenFinalize(root) {
    if (!root) return;

    const imgs = Array.from(root.querySelectorAll('img.product-media__image'));
    if (!imgs.length) {
      MediaGallery.#clearLoadingState(root);
      return;
    }

    let pending = 0;
    imgs.forEach((img) => {
      // If not loaded, wait
      if (!img.complete || img.naturalWidth === 0) {
        pending++;
        const done = () => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          pending--;
          if (pending <= 0) MediaGallery.#clearLoadingState(root);
        };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });

    if (pending === 0) MediaGallery.#clearLoadingState(root);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event?.detail?.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    /**
     * IMPORTANT:
     * this.replaceWith(newMediaGallery) will remove the current element from DOM.
     * So after this line, DO NOT use `this` for DOM operations.
     */
    this.replaceWith(newMediaGallery);

    // Run patch after inserted into DOM
    setTimeout(() => {
      // Use the inserted element reference
      const gallery = newMediaGallery;

      // 1) clear blur/loading state immediately
      MediaGallery.#clearLoadingState(gallery);

      // 2) upgrade images to max res (1920) to avoid temporary softness/blur
      MediaGallery.#upgradeImagesToMax(gallery);

      // 3) finalize once images complete
      MediaGallery.#waitImagesThenFinalize(gallery);

      // Fallback finalize (in case some events don't fire due to cached resources)
      setTimeout(() => MediaGallery.#clearLoadingState(gallery), 800);
    }, 0);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {CustomEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
